/**
 * Отбор шагов для текущего прохода тура (ADR-050 §«Алгоритм отбора», ТЗ §12.1).
 *
 * Чистая функция: без DOM, сети, таймеров и обращений к `Date.now()` иначе как через
 * параметр. Вся логика версионирования и повторного показа живёт здесь, поэтому она
 * покрывается юнит-тестами T-01…T-20 и не требует E2E.
 */

import type { OnboardingManifest, OnboardingStep } from '../manifests/schema';
import type { OnboardingPolicy, OnboardingTourState, SeenStep } from '../types';
import { effectiveSeen, type EffectiveSeen } from './effectiveSeen';
import { evaluatePredicate, type OnboardingContext } from './predicates';
import { compareSemver, parseSemver, versionBump } from './semver';

export interface SelectStepsInput {
  manifest: OnboardingManifest;
  /** Серверное состояние тура; `null` — пользователь тур ещё не начинал. */
  state: OnboardingTourState | null;
  policy: OnboardingPolicy;
  context: OnboardingContext;
  /** Отметки из `localStorage`, ещё не подтверждённые сервером (FR-21). */
  localSeen?: readonly SeenStep[];
  /** `VITE_RELEASE`; сравнивается с `manifest.minAppVersion`. */
  appVersion?: string;
  now?: number;
}

export type SelectionMode = 'fresh' | 'incremental' | 'replayFull' | 'replaySteps' | 'none';

export type SkipReason =
  | 'disabled'
  | 'tourInactive'
  | 'notInRollout'
  | 'appVersionTooLow'
  | 'snoozed'
  | 'nothingToShow';

export interface SelectStepsResult {
  steps: OnboardingStep[];
  mode: SelectionMode;
  skipReason: SkipReason | null;
  /** FR-15: показывать нечего, но состояние стоит закрыть на текущей версии. */
  shouldMarkCompleted: boolean;
  /** Часть кандидатов не влезла в `maxStepsPerSession` и достанется следующей сессии. */
  truncated: boolean;
}

function blocked(reason: SkipReason): SelectStepsResult {
  return { steps: [], mode: 'none', skipReason: reason, shouldMarkCompleted: false, truncated: false };
}

/**
 * Принудительный повтор по серверной политике (FR-09, FR-10).
 *
 * Без `forceReplayAfter` повтор не применяется вовсе: каждый показ обновлял бы
 * `last_seen_at`, снова попадая под правило, и тур зациклился бы. Сервер это правило
 * валидирует на старте, здесь — вторая линия защиты.
 */
function forcedReplay(
  manifest: OnboardingManifest,
  state: OnboardingTourState,
  policy: OnboardingPolicy
): 'tour' | string[] | null {
  if (!policy.forceReplayAfter) return null;

  const threshold = Date.parse(policy.forceReplayAfter);
  const lastSeen = Date.parse(state.lastSeenAt);
  if (Number.isNaN(threshold) || Number.isNaN(lastSeen)) return null;
  if (lastSeen >= threshold) return null;

  if (policy.forceReplayTours.includes(manifest.tourId)) return 'tour';

  const known = new Set(manifest.steps.map((step) => step.id));
  const forced = policy.forceReplaySteps.filter((id) => known.has(id));
  return forced.length > 0 ? forced : null;
}

function needsShowing(step: OnboardingStep, seen: EffectiveSeen): boolean {
  const mark = seen.get(step.id);
  if (!mark) return true;
  // FR-08: существенно переписанный шаг возвращается один, без остального тура.
  return step.resurfaceOnChange && step.rev > mark.rev;
}

/** FR-04: просмотр любого варианта закрывает всю группу взаимоисключающих шагов. */
function seenVariantGroups(manifest: OnboardingManifest, seen: EffectiveSeen): Set<string> {
  const groups = new Set<string>();
  for (const step of manifest.steps) {
    if (!step.variantGroup) continue;
    if (!needsShowing(step, seen)) groups.add(step.variantGroup);
  }
  return groups;
}

function byOrderThenDeclaration(manifest: OnboardingManifest, steps: OnboardingStep[]): OnboardingStep[] {
  const declaration = new Map(manifest.steps.map((step, index) => [step.id, index]));
  return [...steps].sort(
    (a, b) => a.order - b.order || (declaration.get(a.id) ?? 0) - (declaration.get(b.id) ?? 0)
  );
}

function firstPerVariantGroup(steps: OnboardingStep[]): OnboardingStep[] {
  const taken = new Set<string>();
  return steps.filter((step) => {
    if (!step.variantGroup) return true;
    if (taken.has(step.variantGroup)) return false;
    taken.add(step.variantGroup);
    return true;
  });
}

/** Состояние уже закрыто на текущей версии — повторная отметка о завершении не нужна (FR-15). */
function isClosedOnCurrentVersion(manifest: OnboardingManifest, state: OnboardingTourState | null): boolean {
  if (!state) return false;
  return (
    state.status === 'completed' &&
    state.lastVersion === manifest.version &&
    state.campaignId === manifest.campaignId
  );
}

export function selectSteps(input: SelectStepsInput): SelectStepsResult {
  const { manifest, state, policy, context } = input;
  const now = input.now ?? Date.now();

  if (!policy.enabled) return blocked('disabled');
  if (!policy.activeTours.includes(manifest.tourId)) return blocked('tourInactive');
  if (!policy.inRollout) return blocked('notInRollout');

  if (manifest.minAppVersion) {
    const required = parseSemver(manifest.minAppVersion);
    const actual = parseSemver(input.appVersion);
    // Неразбираемая версия сборки не блокирует тур: неизвестный формат `VITE_RELEASE`
    // не должен молча выключать онбординг на всём стенде.
    if (required && actual && compareSemver(actual, required) < 0) return blocked('appVersionTooLow');
  }

  if (state?.snoozedUntil && Date.parse(state.snoozedUntil) > now) return blocked('snoozed');

  const applicable = manifest.steps.filter((step) => evaluatePredicate(step.when, context));
  const seen = effectiveSeen(state?.seenSteps, input.localSeen);

  let mode: SelectionMode;
  let candidates: OnboardingStep[];

  if (!state) {
    mode = 'fresh';
    candidates = applicable;
  } else if (
    state.campaignId !== manifest.campaignId ||
    versionBump(state.lastVersion, manifest.version) === 'major'
  ) {
    // FR-06: смена кампании или MAJOR — тур целиком заново, прошлые отметки не учитываются.
    mode = 'replayFull';
    candidates = applicable;
  } else {
    const forced = forcedReplay(manifest, state, policy);
    if (forced === 'tour') {
      mode = 'replayFull';
      candidates = applicable;
    } else if (forced) {
      mode = 'replaySteps';
      candidates = applicable.filter((step) => forced.includes(step.id));
    } else {
      // MINOR и PATCH обрабатываются одним правилом: показываем применимые шаги, которых
      // нет в реестре просмотренных. PATCH ничего не добавляет и потому ничего не покажет.
      mode = 'incremental';
      const blockedGroups = seenVariantGroups(manifest, seen);
      candidates = applicable.filter(
        (step) => needsShowing(step, seen) && (!step.variantGroup || !blockedGroups.has(step.variantGroup))
      );
    }
  }

  const ordered = firstPerVariantGroup(byOrderThenDeclaration(manifest, candidates));
  const limit = Math.max(0, policy.maxStepsPerSession);
  const steps = ordered.slice(0, limit);

  const shouldMarkCompleted = steps.length === 0 && !isClosedOnCurrentVersion(manifest, state);

  return {
    steps,
    mode: steps.length > 0 ? mode : 'none',
    skipReason: steps.length > 0 ? null : 'nothingToShow',
    shouldMarkCompleted,
    truncated: ordered.length > steps.length,
  };
}
